import './DateTimeService';
import m from './module';
import RecapDefinition from '../model/RecapDefinition';
import { getProvisionalIdentifierForType, resetItemsDisplayOrder, castToBoolean } from '../util';

const dateFields = [
  'recap_available_events_start_time',
  'recap_available_events_end_time',
  'recap_response_available_start_offset',
  'recap_response_available_end_offset',
];

const multiLanguageFields = [
  'name',
];

const recapDefinitionPath = '/recap_definition';
const type = 'RecapDefinition';

class RecapDefinitionService {
  constructor(
    APIService,
    DataSyncService,
    LanguageService,
    $log,
    $q,
    LogService,
    DateTimeService,
    PageService,
    ProgramService,
    LocationVariableService,
    RoleVariableService,
    ValidationService,
    LogicConditionService,
    LogicModifierService,
    FormService,
    FieldService,
    FieldInstanceService,
  ) {
    "ngInject";
    this.APIService = APIService;
    this.DataSyncService = DataSyncService;
    this.LanguageService = LanguageService;
    this.$log = $log;
    this.$q = $q;
    this.LogService = LogService;
    this.DateTimeService = DateTimeService;
    this.PageService = PageService;
    this.ProgramService = ProgramService;
    this.LocationVariableService = LocationVariableService;
    this.RoleVariableService = RoleVariableService;
    this.ValidationService = ValidationService;
    this.LogicConditionService = LogicConditionService;
    this.LogicModifierService = LogicModifierService;
    this.FormService = FormService;
    this.FieldService = FieldService;
    this.FieldInstanceService = FieldInstanceService;
  }

  create(data) {
    const promises = [];

    data = data || {};
    data.id = data.id || getProvisionalIdentifierForType('recap_definition');

    this.LogService.debug(`RecapDefinitionService.create() id=${data.id} using data:`, data);

    // Convert dates - using local browser timezone
    for (const field of dateFields) {
      if (data[field]) {
        data[field] = this.DateTimeService.parseDateWithLocalTimezone(data[field]);
      }
    }
    if (data.created_at) {
      data.created_at = this.DateTimeService.parseDateWithServerTimezone(data.created_at);
    }
    if (data.updated_at) {
      data.updated_at = this.DateTimeService.parseDateWithServerTimezone(data.updated_at);
    }

    // Create pages
    let pagesData = data.pages || [];
    promises.push(
      this.$q
        .all(pagesData.map((pageData, index) => this.$q.when(this.PageService.create({...pageData, index}))))
        .then(pages => data.pages = pages)
    );

    // Create programs
    let programsData = data.programs || [];
    const programCreatePromises = programsData.map(programData => {
      this.LogService.debug(`RecapDefinitionService.create() id=${data.id} creating program:`, programData);
      return this.$q.when(this.ProgramService.create(programData));
    });
    promises.push(
      this.$q.all(programCreatePromises)
        .then(programs => data.programs = programs)
        .then(() => {
          this.LogService.debug(`RecapDefinitionService.create() id=${data.id} created ${data.programs.length} programs`);
        })
    );

    // Create location variables
    let lvsData = data.location_variables || [];
    promises.push(
      this.$q
        .all(lvsData.map(lvData => this.$q.when(this.LocationVariableService.create(lvData))))
        .then(lvs => data.location_variables = lvs)
    );

    // Create role variables
    let rvsData = data.role_variables || [];
    promises.push(
      this.$q
        .all(rvsData.map(rvData => this.$q.when(this.RoleVariableService.createOriginals(rvData))))
        .then(rvs => data.role_variables = rvs)
    );

    return this.$q.all(promises)
      .then(() => {
        const dataWithMultiLanguage = this.LanguageService.populateMultiLanguageFields(data, multiLanguageFields);
        return new RecapDefinition(dataWithMultiLanguage);
      })
      .catch(err => {
        this.LogService.error("Error creating recap definition:", err);
        return this.$q.reject(err);
      })
      .then(recapDefinition => {
        this.LogService.debug(`RecapDefinitionService.create() creating:`, recapDefinition);
        return this._updateFieldInstanceReferences(recapDefinition)
          .then(() => {
            return recapDefinition
          });
      })
      .catch(err => {
        this.LogService.error("Error updating field references:", err);
        return this.$q.reject(err);
      });
  }


  /**
   * Update all references to field instances; this can't be performed until all field instances have been loaded.
   * @param recapDefinition
   * @private
     */
  _updateFieldInstanceReferences(recapDefinition) {
    return this.$q.all([
      ...recapDefinition.findAllValidations().map(validation => this.ValidationService.updateTargetForValidation(validation)),
      ...recapDefinition.findAllLogicConditions().map(logicCondition => this.LogicConditionService.updateTargetForLogicCondition(logicCondition)),
      // A modifier on a choice field compares the watched field's answer with its option (valueOption),
      // which is only set once its target is linked; unlinked, EQUALS never matched in data entry.
      ...recapDefinition.findAllLogicModifiers().map(logicModifier => this.LogicModifierService.updateTargetForLogicModifier(logicModifier)),
    ]);
  }

  getById(id, config = null) {
    return this.DataSyncService.getObjectById(type, recapDefinitionPath, id, data => this.create(data), config)
      .then(recapDefinition => {
        return recapDefinition;
      })
      .catch(response => {
        this.LogService.error("Error retrieving recap:", response);
        return this.$q.reject({
          name: "ErrorLoadingRecapDefinition",
          message: "Error loading recap definition",
          response: response,
        });
      });
  }

  save(recapDefinition) {
    const data = this.serialize(recapDefinition);
    this.LogService.debug("Saving recap definition:", data);

    return this.APIService.post('/recap_definition_check_duplicate_recap_data', data)
      .catch(response => this.$q.reject({
        name: "ErrorSavingRecapDefinition",
        message: "Error saving recap definition",
        response: response,
      }))
      .then((isDuplicateResponse) => {
        if (isDuplicateResponse && isDuplicateResponse.data && castToBoolean(isDuplicateResponse.data.is_duplicate)) {
          let message = '';
          switch (data.type) {
            case 'staff':
              message = 'Could not save recap definition due to there being an existing onboarding recap. Please edit that recap, or save this recap as Inactive.';
              break;
            case 'task':
              message = 'Could not save recap definition due to duplicate task properties.';
              break;
            case 'venue':
            case 'event':
            default:
              message = "Could not save recap definition. Please contact an administrator.";
              break;
          }
          return this.$q.reject({
            name: "ErrorSavingRecapDefinition",
            response: isDuplicateResponse,
            message,
          })
        }

        // Check for any bad ID references
        const assertionResult = this.assertValidIds(recapDefinition)
        if (!assertionResult.passes) {
          let message = 'Could not save recap definition due to invalid or missing references for the following types of objects:<br/><ul>'
          if (assertionResult.isMissingLogicTargets) {
            message += '<li>Logic Targets (e.g. the field or form for something like a logic condition cannot be found)</li>'
          }
          if (assertionResult.isMissingLogicOptions) {
            message += '<li>Logic Options (i.e. an option for a field used by something like a logic condition cannot be found)</li>'
          }
          if (assertionResult.isMissingValidationTargets) {
            message += '<li>Validation Targets (i.e. a field another field references for validation cannot be found)</li>'
          }
          message += '</ul><br/>Please refresh the page and try again. If the problem persists, please contact an administrator.'
          return this.$q.reject({
            name: "ErrorSavingRecapDefinition",
            message: message,
            response: null,
          })
        }

        // Ensure logic conditions either have comparison values or don't require them
        const hasComparisonsResult = this.assertLogicComparisons(recapDefinition)
        if (!hasComparisonsResult) {
          return this.$q.reject({
            name: "ErrorSavingRecapDefinition",
            message: 'Could not save recap definition due to logic conditions missing comparison values',
            response: null,
          })
        }

        return this.APIService.post(recapDefinitionPath, data)
          .catch(response => this.$q.reject({
            name: "ErrorSavingRecapDefinition",
            message: "Error saving recap definition",
            response: response,
          }))
          .then(response => {
            return this.create(response.data);
          })
          .then(async (definition) => {
            const fields = await this.FieldService.forceGetAll()
            if (!fields || !fields.length) {
              return definition;
            }

            // Theis will only do something for Neptune, see NP-488
            const raiCodeInserted = await this.insertTaskFieldIfNeeded(definition, fields, 'FIELD_RAI_CODE', 0);
            const raiReasonCodeInserted = await this.insertTaskFieldIfNeeded(definition, fields, 'FIELD_RAI_REASON_CODE', raiCodeInserted ? 1 : 0);

            if (raiCodeInserted || raiReasonCodeInserted) {
              // We inserted something, so we want to save the recap again to make sure it sticks
              const newDefinitionData = this.serialize(definition);
              const newDefinition = await this.APIService.post(recapDefinitionPath, newDefinitionData)
                .catch(response => this.$q.reject({
                  name: "ErrorSavingRecapDefinition",
                  message: "Error saving recap definition",
                  response: response,
                }))
                .then(response => {
                  return this.create(response.data);
                })
              return newDefinition;
            }

            return definition;
          })
          .catch(err => this.$q.reject({
            name: "ErrorLoadingSavedRecapDefinition",
            message: "Error saving recap definition",
            wrapped: err,
          }));
      })
  }

  async insertTaskFieldIfNeeded(definition, allFields, fieldType, offset = 0) {
    // Keep track of if a page, form, or field was inserted
    let somethingInserted = false;

    // Insert the field if needed
    const needsField = definition.type === 'task' && !definition.pages.some(page => {
      return page.items.some(form => {
        return form.items.some(fieldInstance => {
          return fieldInstance.field.fieldType.defined_as === fieldType
        });
      });
    });
    if (needsField) {
      const fieldFiltered = allFields.filter(field => field.fieldType.defined_as === fieldType);
      const field = fieldFiltered.length ? await this.FieldInstanceService.create({ field: fieldFiltered[0] }) : null;
      if (field) {
        let firstPage = definition.pages.length ? definition.pages[0] : null;
        somethingInserted = true;
        if (!firstPage) {
          const form = await this.FormService.create({ recapDefinitionId: definition.id, items: [ field ] })
          firstPage = await this.PageService.create({ recapDefinitionId: definition.id, items: [ form ] })
          definition.pages.push(firstPage);
        } else {
          let firstForm = definition.pages[0].items && definition.pages[0].items.length ? definition.pages[0].items[0] : null;
          if (!firstForm) {
            firstForm = await this.FormService.create({ recapDefinitionId: definition.id, items: [ field ] });
            firstPage.insertItem(firstForm);
          } else {
            firstForm.insertItem(field, offset);
          }
        }
      }
    }
    return somethingInserted;
  }

  serialize(recapDefinition) {
    resetItemsDisplayOrder(recapDefinition.pages);
    let data = {
      id: recapDefinition.id,
      active: recapDefinition.active,
      pages: recapDefinition.pages.map(page => this.PageService.serialize(page)),
      name: recapDefinition.name,
      name_l10n: recapDefinition.name_l10n,
      recap_available_events_start_time: recapDefinition.recap_available_events_start_time,
      recap_available_events_end_time: recapDefinition.recap_available_events_end_time,
      recap_response_available_start_offset: recapDefinition.recap_response_available_start_offset,
      recap_response_available_end_offset: recapDefinition.recap_response_available_end_offset,
      program_ids: recapDefinition.programs.map(program => program.id),
      event_type_ids: recapDefinition.event_types?.map(eventType => eventType.id) || [],
      location_variable_ids: recapDefinition.location_variables.map(lv => lv.id),
      type: recapDefinition.type,
      version: recapDefinition.version,
      adhoc: recapDefinition.adhoc,
      allow_before_date: recapDefinition.allow_before_date,
      allow_before_date_days: recapDefinition.allow_before_date_days,
      order_id: recapDefinition.order_id,
      product_lines_id: recapDefinition.product_lines_id,
      task_types_id: recapDefinition.task_types_id,
      venue_ids: recapDefinition.venues?.map(venue => venue.id),
      venue_filters: recapDefinition.venue_filters,
      apply_to_all_venues: recapDefinition.apply_to_all_venues,
      customer_ids: recapDefinition.customers?.map(customer => customer.id) || [],
    };

    data = this.LanguageService.populateMultiLanguageFields(data, multiLanguageFields);

    for (let field of dateFields) {
      if (data[field]) {
        data[field] = this.DateTimeService.serializeDateTimeForAPI(data[field]);
      }
    }
    return data;
  }

  assertValidIds(recapDefinition) {
    let formIds = []
    let fieldBlockIds = []
    let fieldInstanceIds = []
    let optionIds = []
    let logicTargetIds = []
    let logicOptionIds = []
    let validationTargetIds = []

    // Get all object IDs that can have references to it from elsewhere
    for (const page of recapDefinition.pages) {
      for (const form of page.items) {
        formIds.push(form.id)
        for (const fieldOrFieldBlock of form.items) {
          if (fieldOrFieldBlock.items) {
            // Field Block
            fieldBlockIds.push(fieldOrFieldBlock.id)
            for (const fieldInstance of fieldOrFieldBlock.items) {
              const {
                optionIds: fieldOptionIds,
                logicTargetIds: fieldLogicTargetIds,
                logicOptionIds: fieldLogicOptionIds,
                validationTargetIds: fieldValidationTargetIds,
              } = this.getIdsFromFieldInstance(fieldInstance)
              fieldInstanceIds.push(fieldInstance.id)
              optionIds.push(...fieldOptionIds)
              logicTargetIds.push(...fieldLogicTargetIds)
              logicOptionIds.push(...fieldLogicOptionIds)
              validationTargetIds.push(...fieldValidationTargetIds)
            }
            for (const logicGroup of fieldOrFieldBlock.logic_groups) {
              for (const logicCondition of logicGroup.conditions) {
                const conditionTargetId = (logicCondition.target && logicCondition.target.id) || logicCondition.target_id
                if (conditionTargetId) {
                  logicTargetIds.push(conditionTargetId)
                }
                if (logicCondition.selected_option_identifier) {
                  logicOptionIds.push(logicCondition.selected_option_identifier)
                }
              }
            }
          } else {
            // Field Instance
            const {
              optionIds: fieldOptionIds,
              logicTargetIds: fieldLogicTargetIds,
              logicOptionIds: fieldLogicOptionIds,
              validationTargetIds: fieldValidationTargetIds,
            } = this.getIdsFromFieldInstance(fieldOrFieldBlock)
            fieldInstanceIds.push(fieldOrFieldBlock.id)
            optionIds.push(...fieldOptionIds)
            logicTargetIds.push(...fieldLogicTargetIds)
            logicOptionIds.push(...fieldLogicOptionIds)
            validationTargetIds.push(...fieldValidationTargetIds)
          }
        }
        for (const logicGroup of form.logic_groups) {
          for (const logicCondition of logicGroup.conditions) {
            const conditionTargetId = (logicCondition.target && logicCondition.target.id) || logicCondition.target_id
            if (conditionTargetId) {
              logicTargetIds.push(conditionTargetId)
            }
            if (logicCondition.selected_option_identifier) {
              logicOptionIds.push(logicCondition.selected_option_identifier)
            }
          }
        }
      }
    }

    // Make sure everything is a string
    formIds = formIds.map(id => String(id))
    fieldBlockIds = fieldBlockIds.map(id => String(id))
    fieldInstanceIds = fieldInstanceIds.map(id => String(id))
    logicTargetIds = logicTargetIds.map(id => String(id))
    optionIds = optionIds.map(id => String(id))
    logicOptionIds = logicOptionIds.map(id => String(id))
    validationTargetIds = validationTargetIds.map(id => String(id))

    // Ensure that every referenced ID has a matching object somewhere
    // If it doesn't, return false, but also tell which object type(s) we're missing
    const isMissingLogicTargets = !logicTargetIds.every(id => fieldInstanceIds.includes(id))
    const isMissingLogicOptions = !logicOptionIds.every(id => optionIds.includes(id))
    const isMissingValidationTargets = !validationTargetIds.every(id => fieldInstanceIds.includes(id))

    if (isMissingLogicTargets || isMissingLogicOptions || isMissingValidationTargets) {
      return {
        passes: false,
        isMissingLogicTargets,
        isMissingLogicOptions,
        isMissingValidationTargets,
      }
    }

    // We're good!
    return {
      passes: true,
    }
  }

  getIdsFromFieldInstance(fieldInstance) {
    const optionIds = []
    const logicTargetIds = []
    const logicOptionIds = []
    const validationTargetIds = []
    if (fieldInstance.field?.options && fieldInstance.field.options.length) {
      for (const option of fieldInstance.field.options) {
        optionIds.push(option.id)
      }
    }
    // Ensure if a validation, logic condition, or logic modifier exist, that they
    // both reference something that exists, returning -1 if not to purposefully fail validation
    for (const logicGroup of fieldInstance.logic_groups) {
      for (const logicCondition of logicGroup.conditions) {
        logicTargetIds.push((logicCondition.target && logicCondition.target.id) || logicCondition.target_id || -1)
        if (logicCondition.usesValue && logicCondition.target && logicCondition.target.field.hasOptions()) {
          // The condition is not checking for "is present"/"required", we have a target field, and said target field has options
          // Implicitly, this means that there must be a selected option for the condition to be valid
          logicOptionIds.push(logicCondition.selected_option_identifier)
        }
      }
      for (const logicModifier of logicGroup.modifiers) {
        logicTargetIds.push((logicModifier.target && logicModifier.target.id) || logicModifier.target_id || -1)
        if (logicModifier.target && logicModifier.target.field.hasOptions()) {
          logicOptionIds.push(logicModifier.selected_events_custom_options_id || -1)
        }
        if (logicModifier.truthy_values) {
          logicOptionIds.push(...logicModifier.truthy_values.map(value => value.id))
        }
        if (logicModifier.falsy_values) {
          logicOptionIds.push(...logicModifier.falsy_values.map(value => value.id))
        }
      }
    }
    for (const validation of fieldInstance.validations) {
      if (validation.type && validation.type.comparesField) {
        validationTargetIds.push((validation.target && validation.target.id) || validation.target_id || -1)
      }
    }
    return {
      optionIds,
      logicTargetIds,
      logicOptionIds,
      validationTargetIds,
    }
  }

  assertLogicComparisons(recapDefinition) {
    for (const page of recapDefinition.pages) {
      for (const form of page.items) {
        for (const fieldOrFieldBlock of form.items) {
          if (fieldOrFieldBlock.items) {
            // Field Block
            for (const fieldInstance of fieldOrFieldBlock.items) {
              for (const logicGroup of fieldInstance.logic_groups) {
                for (const logicCondition of logicGroup.conditions) {
                  if (logicCondition.usesValue && !logicCondition.selected_option_identifier && !logicCondition.value) {
                    return false
                  }
                }
              }
            }
          } else {
            // Field Instance
            for (const logicGroup of fieldOrFieldBlock.logic_groups) {
              for (const logicCondition of logicGroup.conditions) {
                if (logicCondition.usesValue && !logicCondition.selected_option_identifier && !logicCondition.value) {
                  return false
                }
              }
            }
          }
        }
      }
    }
    return true
  }
}

export default RecapDefinitionService;

m.service('RecapDefinitionService', RecapDefinitionService);
